///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { SaleWithCompaniesCountProjection, SuccessorSaleCompaniesToAddProjection, UserDTO } from '../../generated/shared-types.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PagedModel } from '../../generated/spring-types.model';
import { SuiUserDTO } from '../../generated/shared-sui-types.model';
import { Pageable } from '../../services/pageable.model';

@Injectable()
export class UserService {
    private readonly resourceUrl = 'api/users';
    private readonly singleUserResourceUrl = 'api/user';

    constructor(private readonly http: HttpClient) {}

    private static setReqParams(req: Pageable, params?: HttpParams, filter?: string) {
        params = params || new HttpParams();

        if (req && Object.keys(req).length !== 0) {
            params = params.append('page', req.page - 1);
            params = params.append('size', req.size);
        }

        if (filter) {
            params = params.append('filter', filter);
        }

        return params;
    }

    create(user: SuiUserDTO): Observable<SuiUserDTO> {
        return this.http.post<UserDTO>(this.singleUserResourceUrl, user.toDto()).pipe(map((userDto) => new SuiUserDTO(userDto)));
    }

    update(user: SuiUserDTO): Observable<SuiUserDTO> {
        return this.http.put<UserDTO>(this.singleUserResourceUrl, user.toDto()).pipe(map((userDto) => new SuiUserDTO(userDto)));
    }

    findById(id: number): Observable<SuiUserDTO> {
        return this.http.get<UserDTO>(`${this.singleUserResourceUrl}?id=${id}`).pipe(map((userDto) => new SuiUserDTO(userDto)));
    }

    findSaleById(id: number): Observable<SaleWithCompaniesCountProjection> {
        return this.http.get<SaleWithCompaniesCountProjection>(`${this.singleUserResourceUrl}/sale/${id}`);
    }

    delete(id: number): Observable<UserDTO> {
        return this.http.delete<UserDTO>(`${this.singleUserResourceUrl}/${id}`);
    }

    deleteSale(id: number, reassignSaleId?: number): Observable<void> {
        return this.http.delete<void>(`${this.singleUserResourceUrl}/${id}?successorId=${reassignSaleId}`);
    }

    getSuccessorCompaniesList(id: number): Observable<SuccessorSaleCompaniesToAddProjection[]> {
        return this.http.get<SuccessorSaleCompaniesToAddProjection[]>(`${this.resourceUrl}/sales/${id}/successor-companies-count`);
    }

    loadAllCustomersSupport(req?: Pageable): Observable<PagedModel<SuiUserDTO>> {
        return this.loadAllByType('customersSupport', req);
    }

    loadAllSales(req?: Pageable): Observable<PagedModel<SuiUserDTO>> {
        return this.loadAllByType('sales', req);
    }

    loadAllSalesWithoutPaging(): Observable<UserDTO[]> {
        return this.http.get<UserDTO[]>(this.resourceUrl + '/all-sales');
    }

    loadAllAccountants(req?: Pageable): Observable<PagedModel<SuiUserDTO>> {
        return this.loadAllByType('accountants', req);
    }

    loadUsersForCurrentUserCustomer(req?: Pageable): Observable<PagedModel<SuiUserDTO>> {
        return this.loadAllByType('current', req);
    }

    findAllByCustomerId(customerId: number, req?: Pageable, filter?: string): Observable<PagedModel<SuiUserDTO>> {
        return this.http
            .get<PagedModel<UserDTO>>(`${this.resourceUrl}/company=${customerId}`, {
                params: UserService.setReqParams(req, undefined, filter)
            })
            .pipe(
                map((page) => ({
                    ...page,
                    content: page.content.map((userDto) => new SuiUserDTO(userDto))
                }))
            );
    }

    private loadAllByType(userUrl: string, req?: Pageable): Observable<PagedModel<SuiUserDTO>> {
        return this.http
            .get<PagedModel<UserDTO>>(`${this.resourceUrl}/${userUrl}`, {
                params: UserService.setReqParams(req)
            })
            .pipe(
                map((page) => {
                    return {
                        ...page,
                        content: page.content.map((userDto) => {
                            if (userDto['saleId'] !== undefined) {
                                userDto.id = userDto['saleId'];
                            }
                            return new SuiUserDTO(userDto);
                        })
                    };
                })
            );
    }
}
